/**
 * Backend regions Pulse can work against, and the region this page load runs in.
 *
 * Each region is a separate backend stack (Cloud Functions, Firestore database, GraphQL). Dashboards (Superset)
 * are not regional yet: every region uses the home dashboard and its home token function (see cloud.js).
 * The region is fixed for the lifetime of a page load: a sub-admin works in the region stored on their user
 * doc, and login saves it and reloads the app when needed, so every client is created once for the right region.
 * Global data (admin users, roles, master permissions) always lives in the home region — see `homeDb`.
 */
const env = import.meta.env

export const REGIONS = {
  home: {
    key: 'home',
    label: env.VITE_HOME_REGION_LABEL || 'Home',
    isHome: true,
    functionsRegion: env.VITE_FUNCTION_REGION || 'us-central1',
    functionPrefix: 'api-',
    firestoreDb: null, // default database
    graphqlUrl: env.VITE_GRAPHQL_URL,
    // Fallback where a store's company has no country (unchanged from before regions existed).
    defaultCountryCode: 'IN',
  },
  uk: {
    key: 'uk',
    label: env.VITE_UK_REGION_LABEL || 'United Kingdom',
    isHome: false,
    // No defaults: these come only from the environment (set per Vercel project).
    functionsRegion: env.VITE_UK_FUNCTION_REGION,
    functionPrefix: 'api-uk-',
    firestoreDb: env.VITE_UK_FIRESTORE_DB,
    graphqlUrl: env.VITE_UK_GRAPHQL_URL,
    defaultCountryCode: 'GB',
  },
}

/** A non-home region is usable only when its Functions region, Firestore DB and GraphQL URL are all configured. */
const isConfigured = (region) =>
  region.isHome || Boolean(region.functionsRegion && region.firestoreDb && region.graphqlUrl)

export const isRegionAvailable = (key) => Boolean(REGIONS[key] && isConfigured(REGIONS[key]))

export const availableRegions = Object.values(REGIONS).filter(isConfigured)

const STORAGE_KEY = 'selectedRegion'
// Label of the region a switch is reloading into, kept for the first page load after the switch (loading
// message only). Read by name in index.html too, so keep the key in sync.
const SWITCH_KEY = 'regionSwitchTo'
/** Window event fired when a switch starts, so the loading screen can show before the page unloads. */
export const REGION_SWITCH_EVENT = 'chicking:region-switch'

/** The region saved in this browser, or null when nothing valid is saved (or storage is unavailable). */
export const getStoredRegionKey = () => {
  try {
    const key = localStorage.getItem(STORAGE_KEY)
    return isRegionAvailable(key) ? key : null
  } catch {
    return null
  }
}

/** The region this page load runs in. Decided once at startup; never changes without a reload. */
export const currentRegion = REGIONS[getStoredRegionKey() || 'home']

/**
 * Save `key` as the region and reload the app so every client is recreated for it.
 * Returns false (no reload) when the key is unknown/unavailable, already current, or can't be saved —
 * the read-back check prevents a reload loop when storage is blocked.
 */
export const switchRegion = (key) => {
  if (!isRegionAvailable(key)) return false
  try {
    localStorage.setItem(STORAGE_KEY, key)
    if (localStorage.getItem(STORAGE_KEY) !== key) return false
  } catch {
    return false
  }
  if (key === currentRegion.key) return false
  try {
    sessionStorage.setItem(SWITCH_KEY, REGIONS[key].label)
  } catch {
    // storage unavailable — only the loading message after the reload is affected
  }
  window.dispatchEvent(new CustomEvent(REGION_SWITCH_EVENT, { detail: { label: REGIONS[key].label } }))
  // Let the loading screen paint before the page unloads.
  setTimeout(() => window.location.reload(), 50)
  return true
}

/** Label of the region the last switch reloaded into, while that first load is still running; else null. */
export const getPendingRegionSwitch = () => {
  try {
    return sessionStorage.getItem(SWITCH_KEY) || null
  } catch {
    return null
  }
}

export const clearPendingRegionSwitch = () => {
  try {
    sessionStorage.removeItem(SWITCH_KEY)
  } catch {
    // storage unavailable — nothing to clear
  }
}

/** Forget the saved region (e.g. on logout); the next login decides the region again. */
export const clearStoredRegion = () => {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // storage unavailable — nothing to clear
  }
}

/** localStorage key for a saved selection (e.g. 'selectedStore'), per region. Home keeps the original key. */
export const regionStorageKey = (name) => (currentRegion.isHome ? name : `${name}:${currentRegion.key}`)
